import { useEffect, useRef, useCallback, useState } from 'react';
import { useDeviceInfo } from '@/lib/deviceDetect';
// Use a compact height on mobile — 220px keeps the grid impressive without a large gap
const MOBILE_GRID_H = 220;

// ── Agent nodes that appear on the network ────────────────────────────────────
const AGENT_NODES = [
  { label: 'ARIA-7',       dept: 'ENTERPRISE',  color: '#00A6EB' },
  { label: 'NEXUS-CHIEF',  dept: 'SMALL BIZ',   color: '#A855F7' },
  { label: 'CIPHER',       dept: 'DATA',         color: '#00FFFF' },
  { label: 'LYRA-X',       dept: 'MARKETING',   color: '#FF0055' },
  { label: 'MERIDIAN',     dept: 'OPS',          color: '#00FF88' },
  { label: 'NOVA-9',       dept: 'TECH',         color: '#00FF88' },
  { label: 'TAXON-AI',     dept: 'FINANCE',      color: '#FFB800' },
  { label: 'LEX-SCOUT',    dept: 'LEGAL',        color: '#FF6B35' },
];

const PALETTE = ['#00A6EB', '#A855F7', '#FF0055', '#00FF88', '#FFB800', '#00FFFF', '#FF6B35'];

interface Node {
  x: number; y: number;
  vx: number; vy: number;
  r: number;
  color: string;
  pulse: number;
  pulseSpeed: number;
  agentIdx: number; // -1 = generic node
  ring: number;     // ring animation phase
  glowBurst: number; // 0–1 burst intensity, decays each frame
}

interface Edge {
  a: number; b: number;
  pulseT: number; // 0–1 position of traveling dot
  pulseSpeed: number;
  active: boolean;
}

interface RainDrop {
  x: number; y: number;
  speed: number;
  length: number;
  opacity: number;
  color: string;
}

interface Ripple {
  x: number; y: number;
  r: number;
  maxR: number;
  alpha: number;
  color: string;
}

export default function NeuralDeploymentGrid() {
  const device = useDeviceInfo();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef   = useRef<number>(0);
  const stateRef  = useRef<{
    nodes: Node[];
    edges: Edge[];
    rain: RainDrop[];
    ripples: Ripple[];
    w: number; h: number;
    reduced: boolean;
    frame: number;
  } | null>(null);

  // ── Build initial state ──────────────────────────────────────────────────────
  const buildState = useCallback((w: number, h: number, reduced: boolean) => {
    const NODE_COUNT = 38;
    const AGENT_COUNT = AGENT_NODES.length;

    // Nodes
    const nodes: Node[] = Array.from({ length: NODE_COUNT }, (_, i) => {
      const isAgent = i < AGENT_COUNT;
      const color = isAgent ? AGENT_NODES[i].color : PALETTE[Math.floor(Math.random() * PALETTE.length)];
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * (reduced ? 0.08 : 0.22),
        vy: (Math.random() - 0.5) * (reduced ? 0.08 : 0.22),
        r: isAgent ? 5.5 : 2.5 + Math.random() * 2,
        color,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.018 + Math.random() * 0.025,
        agentIdx: isAgent ? i : -1,
        ring: Math.random() * Math.PI * 2,
        glowBurst: 0,
      };
    });

    // Edges — connect each node to 2–3 nearest neighbours
    const edges: Edge[] = [];
    const MAX_DIST = Math.min(w, h) * 0.42;
    for (let i = 0; i < NODE_COUNT; i++) {
      const dists = nodes
        .map((n, j) => ({ j, d: Math.hypot(n.x - nodes[i].x, n.y - nodes[i].y) }))
        .filter(({ j, d }) => j !== i && d < MAX_DIST)
        .sort((a, b) => a.d - b.d)
        .slice(0, 3);
      for (const { j } of dists) {
        if (!edges.find(e => (e.a === i && e.b === j) || (e.a === j && e.b === i))) {
          edges.push({
            a: i, b: j,
            pulseT: Math.random(),
            pulseSpeed: (reduced ? 0.003 : 0.007) + Math.random() * 0.006,
            active: true,
          });
        }
      }
    }

    // Rain drops — vertical data streams
    const RAIN_COUNT = reduced ? 0 : 28;
    const rain: RainDrop[] = Array.from({ length: RAIN_COUNT }, () => ({
      x: Math.random() * w,
      y: Math.random() * h - h,
      speed: 0.6 + Math.random() * 1.4,
      length: 18 + Math.random() * 45,
      opacity: 0.06 + Math.random() * 0.14,
      color: PALETTE[Math.floor(Math.random() * PALETTE.length)],
    }));

    stateRef.current = { nodes, edges, rain, ripples: [], w, h, reduced, frame: 0 };
  }, []);

  // ── Touch ripple ─────────────────────────────────────────────────────────────
  const addRipple = useCallback((x: number, y: number) => {
    if (!stateRef.current) return;
    stateRef.current.ripples.push({
      x, y, r: 0,
      maxR: Math.min(stateRef.current.w, stateRef.current.h) * 0.55,
      alpha: 0.9,
      color: '#00A6EB',
    });
    // Boost nearby nodes
    for (const node of stateRef.current.nodes) {
      const d = Math.hypot(node.x - x, node.y - y);
      if (d < 120) {
        node.pulse += Math.PI;
        node.ring += Math.PI;
      }
    }
  }, []);

  // ── Draw frame ───────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const s = stateRef.current;
    if (!canvas || !s) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { w, h, nodes, edges, rain, ripples } = s;
    s.frame++;

    // ── Clear ──
    ctx.clearRect(0, 0, w, h);

    // ── Background ──
    const bg = ctx.createLinearGradient(0, 0, w, h);
    bg.addColorStop(0,   'rgba(2,4,14,1)');
    bg.addColorStop(0.5, 'rgba(4,6,18,1)');
    bg.addColorStop(1,   'rgba(6,2,16,1)');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // ── Ambient radial glow (Blade Runner wet-neon city glow) ──
    const glow1 = ctx.createRadialGradient(w * 0.25, h * 0.4, 0, w * 0.25, h * 0.4, w * 0.5);
    glow1.addColorStop(0, 'rgba(0,166,235,0.07)');
    glow1.addColorStop(1, 'transparent');
    ctx.fillStyle = glow1;
    ctx.fillRect(0, 0, w, h);

    const glow2 = ctx.createRadialGradient(w * 0.75, h * 0.6, 0, w * 0.75, h * 0.6, w * 0.45);
    glow2.addColorStop(0, 'rgba(168,85,247,0.06)');
    glow2.addColorStop(1, 'transparent');
    ctx.fillStyle = glow2;
    ctx.fillRect(0, 0, w, h);

    // ── Data rain ──
    for (const drop of rain) {
      drop.y += drop.speed;
      if (drop.y > h + drop.length) { drop.y = -drop.length; drop.x = Math.random() * w; }
      const grad = ctx.createLinearGradient(drop.x, drop.y - drop.length, drop.x, drop.y);
      grad.addColorStop(0, 'transparent');
      grad.addColorStop(1, drop.color + Math.floor(drop.opacity * 255).toString(16).padStart(2, '0'));
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(drop.x, drop.y - drop.length);
      ctx.lineTo(drop.x, drop.y);
      ctx.stroke();
    }

    // ── Edges ──
    for (const edge of edges) {
      const a = nodes[edge.a];
      const b = nodes[edge.b];
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      const maxDist = Math.min(w, h) * 0.42;
      const alpha = Math.max(0, 1 - dist / maxDist) * 0.22;

      // Edge line
      ctx.strokeStyle = `rgba(0,166,235,${alpha})`;
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();

      // Traveling pulse dot
      edge.pulseT = (edge.pulseT + edge.pulseSpeed) % 1;
      const px = a.x + (b.x - a.x) * edge.pulseT;
      const py = a.y + (b.y - a.y) * edge.pulseT;
      const pColor = a.agentIdx >= 0 ? a.color : (b.agentIdx >= 0 ? b.color : '#00A6EB');
      ctx.beginPath();
      ctx.arc(px, py, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = pColor + 'CC';
      ctx.shadowBlur = 6;
      ctx.shadowColor = pColor;
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    // ── Nodes ──
    for (const node of nodes) {
      // Drift
      node.x += node.vx;
      node.y += node.vy;
      if (node.x < 0 || node.x > w) node.vx *= -1;
      if (node.y < 0 || node.y > h) node.vy *= -1;
      node.pulse += node.pulseSpeed;
      node.ring  += node.pulseSpeed * 0.7;
      // Decay glow burst — 0.055/frame ≈ 200ms at 60fps
      if (node.glowBurst > 0) node.glowBurst = Math.max(0, node.glowBurst - 0.055);

      const pulseScale = 0.7 + 0.3 * Math.sin(node.pulse);
      const isAgent = node.agentIdx >= 0;

      if (isAgent) {
        // Double ring for agent nodes
        const ringR1 = node.r + 5 + 3 * Math.sin(node.ring);
        const ringR2 = node.r + 10 + 4 * Math.sin(node.ring + 1.2);
        ctx.beginPath();
        ctx.arc(node.x, node.y, ringR1, 0, Math.PI * 2);
        ctx.strokeStyle = node.color + '55';
        ctx.lineWidth = 0.8;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(node.x, node.y, ringR2, 0, Math.PI * 2);
        ctx.strokeStyle = node.color + '28';
        ctx.lineWidth = 0.5;
        ctx.stroke();
      }

      // Node glow
      const glow = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.r * 3.5 * pulseScale);
      glow.addColorStop(0, node.color + 'AA');
      glow.addColorStop(1, 'transparent');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.r * 3.5 * pulseScale, 0, Math.PI * 2);
      ctx.fill();
      // Glow burst overlay — expands radial gradient when burst > 0
      if (node.glowBurst > 0) {
        const burstR = node.r * (3.5 + 8 * node.glowBurst) * pulseScale;
        const burstGlow = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, burstR);
        burstGlow.addColorStop(0, node.color + Math.floor(node.glowBurst * 0xCC).toString(16).padStart(2, '0'));
        burstGlow.addColorStop(0.4, node.color + Math.floor(node.glowBurst * 0x55).toString(16).padStart(2, '0'));
        burstGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = burstGlow;
        ctx.beginPath();
        ctx.arc(node.x, node.y, burstR, 0, Math.PI * 2);
        ctx.fill();
      }

      // Node core
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.r * pulseScale, 0, Math.PI * 2);
      ctx.fillStyle = node.color;
      ctx.shadowBlur = isAgent ? (18 + node.glowBurst * 32) : 8;
      ctx.shadowColor = node.color;
      ctx.fill();
      ctx.shadowBlur = 0;

      // Agent label
      if (isAgent) {
        const agent = AGENT_NODES[node.agentIdx];
        const labelAlpha = 0.55 + 0.45 * Math.sin(node.pulse * 0.5);
        ctx.font = `700 8px 'Orbitron', monospace`;
        ctx.fillStyle = node.color + Math.floor(labelAlpha * 255).toString(16).padStart(2, '0');
        ctx.shadowBlur = 6;
        ctx.shadowColor = node.color;
        ctx.fillText(agent.label, node.x + node.r + 5, node.y - 3);
        ctx.font = `500 6px 'Share Tech Mono', monospace`;
        ctx.fillStyle = node.color + '70';
        ctx.shadowBlur = 0;
        ctx.fillText(agent.dept, node.x + node.r + 5, node.y + 7);
      }
    }

    // ── Ripples ──
    for (let i = ripples.length - 1; i >= 0; i--) {
      const rip = ripples[i];
      rip.r += s.reduced ? 2 : 4.5;
      rip.alpha *= 0.93;
      if (rip.alpha < 0.01 || rip.r > rip.maxR) { ripples.splice(i, 1); continue; }
      // Outer ring
      ctx.beginPath();
      ctx.arc(rip.x, rip.y, rip.r, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(0,166,235,${rip.alpha * 0.8})`;
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 12;
      ctx.shadowColor = '#00A6EB';
      ctx.stroke();
      ctx.shadowBlur = 0;
      // Inner ring (offset)
      if (rip.r > 20) {
        ctx.beginPath();
        ctx.arc(rip.x, rip.y, rip.r * 0.6, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(168,85,247,${rip.alpha * 0.5})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }
    }

    // ── HUD Overlay ──
    // Corner brackets
    const bSize = 14; const bGap = 8;
    ctx.strokeStyle = 'rgba(0,166,235,0.55)';
    ctx.lineWidth = 1.5;
    // TL
    ctx.beginPath(); ctx.moveTo(bGap + bSize, bGap); ctx.lineTo(bGap, bGap); ctx.lineTo(bGap, bGap + bSize); ctx.stroke();
    // TR
    ctx.beginPath(); ctx.moveTo(w - bGap - bSize, bGap); ctx.lineTo(w - bGap, bGap); ctx.lineTo(w - bGap, bGap + bSize); ctx.stroke();
    // BL
    ctx.beginPath(); ctx.moveTo(bGap + bSize, h - bGap); ctx.lineTo(bGap, h - bGap); ctx.lineTo(bGap, h - bGap - bSize); ctx.stroke();
    // BR
    ctx.beginPath(); ctx.moveTo(w - bGap - bSize, h - bGap); ctx.lineTo(w - bGap, h - bGap); ctx.lineTo(w - bGap, h - bGap - bSize); ctx.stroke();

    // Status dot + label (top-left)
    const dotPulse = 0.6 + 0.4 * Math.sin(s.frame * 0.04);
    ctx.beginPath();
    ctx.arc(bGap + 5, bGap + 24, 3, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(0,255,136,${dotPulse})`;
    ctx.shadowBlur = 8;
    ctx.shadowColor = '#00FF88';
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.font = `600 7px 'Share Tech Mono', monospace`;
    ctx.fillStyle = `rgba(0,255,136,${dotPulse * 0.9})`;
    ctx.fillText('◈ NEURAL GRID — LIVE', bGap + 12, bGap + 27);

    // Agent count (bottom-right)
    ctx.font = `700 8px 'Orbitron', monospace`;
    ctx.fillStyle = 'rgba(0,166,235,0.65)';
    ctx.textAlign = 'right';
    ctx.fillText('232 AGENTS ACTIVE', w - bGap - 2, h - bGap - 4);
    ctx.textAlign = 'left';

    // Scan line (horizontal sweep)
    const scanY = ((s.frame * 0.7) % (h + 40)) - 20;
    const scanGrad = ctx.createLinearGradient(0, scanY - 2, 0, scanY + 2);
    scanGrad.addColorStop(0, 'transparent');
    scanGrad.addColorStop(0.5, 'rgba(0,166,235,0.12)');
    scanGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = scanGrad;
    ctx.fillRect(0, scanY - 2, w, 4);

    // Top + bottom edge glow lines
    const edgeGlow = ctx.createLinearGradient(0, 0, w, 0);
    edgeGlow.addColorStop(0, 'transparent');
    edgeGlow.addColorStop(0.3, 'rgba(0,166,235,0.4)');
    edgeGlow.addColorStop(0.7, 'rgba(168,85,247,0.4)');
    edgeGlow.addColorStop(1, 'transparent');
    ctx.fillStyle = edgeGlow;
    ctx.fillRect(0, 0, w, 1);
    ctx.fillRect(0, h - 1, w, 1);

    animRef.current = requestAnimationFrame(draw);
  }, []);

  // ── Setup & resize ───────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const setup = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      // Cap DPR at 1 on low-perf devices (older Android, WebView) to maintain 60fps
      const maxDpr = device.isLowPerfDevice ? 1 : 2;
      const dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
      canvas.width  = w * dpr;
      canvas.height = h * dpr;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);
      // Use reduced mode for low-perf devices OR prefers-reduced-motion
      buildState(w, h, reduced || device.isLowPerfDevice);
    };

    setup();
    animRef.current = requestAnimationFrame(draw);

    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(animRef.current);
      setup();
      animRef.current = requestAnimationFrame(draw);
    });
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
    };
  }, [buildState, draw]);

  // ── Touch handler ────────────────────────────────────────────────────────────
  const handleTouch = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const tx = touch.clientX - rect.left;
    const ty = touch.clientY - rect.top;
    addRipple(tx, ty);
    setPromptVisible(false);

    // ── Haptic feedback: fire on agent node tap (Android Vibration API) ─────────
    // iOS Safari blocks navigator.vibrate — the try/catch ensures silent fallback
    if (stateRef.current && 'vibrate' in navigator) {
      const HIT_RADIUS = 44; // px — generous tap target for agent nodes
      const hitAgent = stateRef.current.nodes.find(n =>
        n.agentIdx >= 0 &&
        Math.hypot(n.x - tx, n.y - ty) < HIT_RADIUS
      );
      if (hitAgent) {
        // Named agent node hit — crisp selection tap (12ms = Apple Taptic Engine selection)
        try { navigator.vibrate(12); } catch { /* iOS Safari: vibrate not supported */ }
      } else {
        // General canvas tap — subtle micro-pulse (6ms)
        try { navigator.vibrate(6); } catch { /* iOS Safari: vibrate not supported */ }
      }
    }

    // ── Glow burst: trigger on agent node tap ────────────────────────────────────
    if (stateRef.current) {
      const HIT_RADIUS = 44;
      const burstNode = stateRef.current.nodes.find(n =>
        n.agentIdx >= 0 &&
        Math.hypot(n.x - tx, n.y - ty) < HIT_RADIUS
      );
      if (burstNode) burstNode.glowBurst = 1.0;
    }
  }, [addRipple]);

  // ── iOS Safari: prevent double-tap zoom on canvas
  // (touchAction: pan-y already set on canvas element)

  // ── TOUCH TO ACTIVATE prompt — fades after 3s, dismissed on first touch ─────
  const [promptVisible, setPromptVisible] = useState(true);
  const [promptOpacity, setPromptOpacity] = useState(1);
  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setPromptOpacity(0);
      setTimeout(() => setPromptVisible(false), 700);
    }, 3000);
    return () => clearTimeout(fadeTimer);
  }, []);

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: MOBILE_GRID_H,
      overflow: 'hidden',
      background: 'rgba(2,4,14,1)',
    }}>
      <canvas
        ref={canvasRef}
        onTouchStart={handleTouch}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          touchAction: 'pan-y',
        }}
      />
      {/* Vignette edges */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 100% 100% at 50% 50%, transparent 60%, rgba(2,4,14,0.7) 100%)',
      }} />
      {/* Bottom fade into page */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 40,
        background: 'linear-gradient(to bottom, transparent, rgba(4,8,20,0.95))',
        pointerEvents: 'none',
      }} />
      {/* TOUCH TO ACTIVATE prompt */}
      {promptVisible && (
        <div style={{
          position: 'absolute',
          bottom: 48,
          left: '50%',
          transform: 'translateX(-50%)',
          opacity: promptOpacity,
          transition: 'opacity 0.7s ease',
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          background: 'rgba(0,166,235,0.08)',
          border: '1px solid rgba(0,166,235,0.3)',
          borderRadius: 2,
          padding: '0.3rem 0.85rem',
          whiteSpace: 'nowrap',
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#00A6EB',
            boxShadow: '0 0 8px #00A6EB, 0 0 16px rgba(0,166,235,0.4)',
            animation: 'neonPulseCyan 1.2s ease-in-out infinite',
            flexShrink: 0,
          }} />
          <span style={{
            fontFamily: 'Share Tech Mono, monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.2em',
            color: 'rgba(0,166,235,0.75)',
            textTransform: 'uppercase',
          }}>
            TOUCH TO ACTIVATE
          </span>
        </div>
      )}
    </div>
  );
}
